import React, { useState, useRef } from 'react';
import { Save, Trash2, Download, Upload, ExternalLink, Edit2, Check, X, Search, AlertTriangle, ChevronDown, FileText, Image } from 'lucide-react';
import { useToast } from './ToastNotifications';
import PageHeader from './PageHeader';
import PaletteDisplay from './PaletteDisplay';
import CompactExportDropdown from './CompactExportDropdown';
import ConfirmationModal from './ConfirmationModal';
import jsPDF from 'jspdf';

const SavedPalettes = ({
  selectedColor,
  onColorClick,
  visibleFields = [],
  colorBlindnessType = 'none',
  isDark = false,
  addColorToPalette,
  setVisualizerColors,
  savedPalettes = [],
  setSavedPalettes,
  addColorToCustomPalette,
  customPalette = [],
  setCustomPalette
}) => {
  const { addToast } = useToast();
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, paletteId: null, paletteName: '' });
  const [editingPaletteId, setEditingPaletteId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setIsExportDropdownOpen(false);
      }
    };

    if (isExportDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExportDropdownOpen]);

  const deletePalette = (id, name) => {
    setDeleteConfirmation({ isOpen: true, paletteId: id, paletteName: name });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.paletteId) {
      setSavedPalettes(savedPalettes.filter(p => p.id !== deleteConfirmation.paletteId));
      addToast(`Deleted "${deleteConfirmation.paletteName}"`, 'success');
    }
  };

  const startRename = (id, currentName) => {
    setEditingPaletteId(id);
    setEditingName(currentName);
  };

  const cancelRename = () => {
    setEditingPaletteId(null);
    setEditingName('');
  };

  const saveRename = (id) => {
    if (!editingName.trim()) {
      addToast('Palette name cannot be empty', 'error');
      return;
    }

    setSavedPalettes(savedPalettes.map(p =>
      p.id === id ? { ...p, name: editingName.trim() } : p
    ));
    addToast('Palette renamed successfully', 'success');
    setEditingPaletteId(null);
    setEditingName('');
  };


  const exportPalettesJSON = () => {
    const dataStr = JSON.stringify(savedPalettes, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `color-palettes-backup-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    addToast(`Exported ${savedPalettes.length} palette(s) as JSON backup`, 'success');
    setIsExportDropdownOpen(false);
  };

  const exportAllAsPDF = () => {
    if (savedPalettes.length === 0) {
      addToast('No palettes to export', 'error');
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    savedPalettes.forEach((palette, paletteIndex) => {
      if (paletteIndex > 0) {
        doc.addPage();
      }

      // Title
      doc.setFontSize(24);
      doc.setTextColor(31, 41, 55);
      doc.text(palette.name, margin, margin + 10);

      // Subtitle
      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128);
      doc.text(`${palette.colors.length} colors • Page ${paletteIndex + 1} of ${savedPalettes.length}`, margin, margin + 18);

      // Calculate grid layout
      const numColors = palette.colors.length;
      let cols, rows;

      if (numColors <= 6) {
        cols = numColors;
        rows = 1;
      } else if (numColors <= 12) {
        cols = 6;
        rows = 2;
      } else {
        cols = 6;
        rows = Math.ceil(numColors / 6);
      }

      // Calculate swatch dimensions
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      const swatchAreaY = margin + 30;
      const swatchAreaHeight = contentHeight - 50;
      const gap = 3;
      const swatchWidth = (contentWidth - (gap * (cols - 1))) / cols;
      const swatchHeight = (swatchAreaHeight - (gap * (rows - 1))) / rows;

      // Draw color swatches
      palette.colors.forEach((color, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);

        const x = margin + (col * (swatchWidth + gap));
        const y = swatchAreaY + (row * (swatchHeight + gap));

        // Parse hex color
        const hex = color.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Draw swatch
        doc.setFillColor(r, g, b);
        doc.roundedRect(x, y, swatchWidth, swatchHeight - 15, 2, 2, 'F');

        // Add color code
        doc.setFontSize(9);
        doc.setTextColor(60, 60, 60);
        const textX = x + (swatchWidth / 2);
        doc.text(color.toUpperCase(), textX, y + swatchHeight - 8, { align: 'center' });

        // Add color number
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text(`Color ${index + 1}`, textX, y + swatchHeight - 3, { align: 'center' });
      });

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text('Generated with Komorebi • www.ko-mo-re-bi.com', pageWidth / 2, pageHeight - 8, { align: 'center' });
    });

    doc.save(`all-palettes-${Date.now()}.pdf`);
    addToast(`Exported ${savedPalettes.length} palette(s) as PDF`, 'success');
    setIsExportDropdownOpen(false);
  };

  const exportAllAsSVG = () => {
    if (savedPalettes.length === 0) {
      addToast('No palettes to export', 'error');
      return;
    }

    // Create a combined SVG with all palettes
    const swatchSize = 80;
    const gap = 8;
    const padding = 20;
    const textHeight = 25;
    const headerHeight = 50;
    const palettePadding = 40;

    let currentY = padding;
    const maxWidth = 800;

    // Calculate total height needed
    let totalHeight = padding * 2;
    savedPalettes.forEach(palette => {
      const cols = Math.min(palette.colors.length, 8);
      const rows = Math.ceil(palette.colors.length / cols);
      totalHeight += headerHeight + (rows * (swatchSize + textHeight)) + ((rows - 1) * gap) + palettePadding;
    });

    let svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${maxWidth}" height="${totalHeight}" viewBox="0 0 ${maxWidth} ${totalHeight}" xmlns="http://www.w3.org/2000/svg">
  <!-- Generated with Komorebi Color Palette Generator -->
  <!-- www.ko-mo-re-bi.com -->
  <defs>
    <style>
      .palette-title { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 18px; font-weight: 600; fill: #1f2937; }
      .palette-subtitle { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 11px; fill: #6b7280; }
      .color-label { font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace; font-size: 10px; fill: #374151; }
      .color-name { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 8px; fill: #9ca3af; }
    </style>
  </defs>

  <rect width="${maxWidth}" height="${totalHeight}" fill="#f9fafb" rx="8"/>
`;

    savedPalettes.forEach((palette, paletteIndex) => {
      const cols = Math.min(palette.colors.length, 8);
      const rows = Math.ceil(palette.colors.length / cols);

      svg += `  <g id="palette-${paletteIndex + 1}">
    <text x="${padding}" y="${currentY + 20}" class="palette-title">${palette.name}</text>
    <text x="${padding}" y="${currentY + 35}" class="palette-subtitle">${palette.colors.length} colors</text>
`;

      palette.colors.forEach((color, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);

        const x = padding + (col * (swatchSize + gap));
        const y = currentY + headerHeight + (row * (swatchSize + textHeight + gap));

        svg += `    <g id="palette-${paletteIndex + 1}-color-${index + 1}">
      <rect x="${x}" y="${y}" width="${swatchSize}" height="${swatchSize}" fill="${color}" rx="4"/>
      <text x="${x + swatchSize / 2}" y="${y + swatchSize + 14}" text-anchor="middle" class="color-label">${color.toUpperCase()}</text>
      <text x="${x + swatchSize / 2}" y="${y + swatchSize + 22}" text-anchor="middle" class="color-name">Color ${index + 1}</text>
    </g>
`;
      });

      svg += `  </g>
`;

      currentY += headerHeight + (rows * (swatchSize + textHeight)) + ((rows - 1) * gap) + palettePadding;
    });

    svg += `  <text x="${maxWidth / 2}" y="${totalHeight - 15}" text-anchor="middle" class="palette-subtitle">Generated with Komorebi • www.ko-mo-re-bi.com</text>
</svg>`;

    // Download SVG
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `all-palettes-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    addToast(`Exported ${savedPalettes.length} palette(s) as SVG`, 'success');
    setIsExportDropdownOpen(false);
  };

  const importPalettes = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        if (Array.isArray(imported)) {
          // Merge imported palettes with existing ones, avoiding duplicates
          const merged = [...imported, ...savedPalettes];
          const unique = merged.filter((palette, index, self) =>
            index === self.findIndex(p => p.id === palette.id)
          );
          setSavedPalettes(unique);
          addToast(`Successfully imported ${imported.length} palette(s)`, 'success');
        } else {
          addToast('Invalid palette file format', 'error');
        }
      } catch (error) {
        addToast('Error reading palette file', 'error');
        console.error(error);
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const handleExportToVisualizer = (colors, paletteName, event) => {
    const colorObjects = colors.map((color, index) => ({
      id: `${paletteName.toLowerCase().replace(/\s+/g, '-')}-${index}`,
      value: color
    }));
    setVisualizerColors(colorObjects, event.target);
  };

  const handleSaveCurrentPalette = () => {
    if (!customPalette || customPalette.length === 0) {
      addToast('No colors in custom palette to save', 'error');
      return;
    }

    // Use default name but add a number if there are existing palettes with the same name
    const baseName = 'My Custom Palette';
    const existingNames = savedPalettes.map(p => p.name);
    let paletteName = baseName;
    let counter = 2;

    while (existingNames.includes(paletteName)) {
      paletteName = `${baseName} ${counter}`;
      counter++;
    }

    const newPalette = {
      id: Date.now(),
      name: paletteName,
      colors: [...customPalette],
      createdAt: new Date().toISOString()
    };

    setSavedPalettes([newPalette, ...savedPalettes]);
    setCustomPalette([]);
    addToast(`Saved "${paletteName}" with ${customPalette.length} colors`, 'success');
  };

  const handleExportCurrentToVisualizer = (event) => {
    if (!customPalette || customPalette.length === 0) {
      addToast('No colors in custom palette to export', 'error');
      return;
    }

    const colorObjects = customPalette.map((color, index) => ({
      id: `custom-palette-${index}`,
      value: color
    }));
    setVisualizerColors(colorObjects, event.target);
  };

  const removeColorFromCustomPalette = (color) => {
    setCustomPalette(customPalette.filter(c => c !== color));
  };

  const generatePaletteCss = (colors, paletteName) => {
    const cssVarName = paletteName.toLowerCase().replace(/\s+/g, '-');
    return `:root {\n${colors.map((color, i) => `  --${cssVarName}-${i + 1}: ${color};`).join('\n')}\n}`;
  };

  const generatePaletteSvg = (colors, paletteName) => {
    const width = colors.length * 60;
    const height = 80;
    return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <rect width="${width}" height="${height}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8"/>
  ${colors.map((color, i) => `
  <rect x="${i * 60 + 10}" y="10" width="50" height="50" fill="${color}" stroke="#fff" stroke-width="2" rx="4"/>
  <text x="${i * 60 + 35}" y="72" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#666">${color}</text>`).join('')}
</svg>`;
  };

  // Filter palettes based on search query
  const filteredPalettes = savedPalettes.filter(palette =>
    palette.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    palette.colors.some(color => color.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="overflow-visible">
      <PageHeader
        title="Saved Palettes"
        description="Save and manage your color palettes"
        actions={
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={importPalettes}
              accept=".json"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
            >
              <Upload className="w-4 h-4" />
              Import
            </button>

            {/* Export All Dropdown */}
            <div className="relative" ref={exportDropdownRef}>
              <button
                onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
                disabled={savedPalettes.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export All
                <ChevronDown className="w-3 h-3" />
              </button>

              {isExportDropdownOpen && savedPalettes.length > 0 && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50">
                  <div className="p-2">
                    <button
                      onClick={exportPalettesJSON}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Download className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">Backup (JSON)</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Save all palettes data</div>
                      </div>
                    </button>

                    <button
                      onClick={exportAllAsPDF}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <FileText className="w-4 h-4 text-red-600 dark:text-red-400" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">PDF Document</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Print-ready, one page per palette</div>
                      </div>
                    </button>

                    <button
                      onClick={exportAllAsSVG}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <Image className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      <div className="flex-1 text-left">
                        <div className="font-medium">SVG Vector</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">For Figma, Illustrator, Sketch</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        }
      />

      {/* Local Storage Warning */}
      <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
              Local Storage Notice
            </h4>
            <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
              Your palettes are stored locally in your browser. If you clear your browser data, use incognito mode, or switch devices/browsers, your saved palettes may be lost.
              <span className="font-medium"> We recommend exporting your palettes regularly</span> using the "Export All" button above to keep a backup.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Search Filter */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search palettes by name or color..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Current Custom Palette */}
        {customPalette && customPalette.length > 0 && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border-2 border-blue-200 dark:border-blue-800 overflow-hidden">
            <div className="p-4 border-b border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Current Custom Palette
                    </h4>
                    <span className="px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                      Building
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {customPalette.length} colors • Right-click any color to add more
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={handleSaveCurrentPalette}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                  >
                    <Save className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={handleExportCurrentToVisualizer}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Visualizer
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4">
              <PaletteDisplay
                colors={customPalette}
                onColorClick={onColorClick}
                baseColor={selectedColor}
                visibleFields={visibleFields}
                colorBlindnessType={colorBlindnessType}
                isDark={isDark}
                addColorToPalette={addColorToPalette}
                addColorToCustomPalette={addColorToCustomPalette}
                onRemoveColor={removeColorFromCustomPalette}
              />
            </div>
          </div>
        )}

        {/* Saved Palettes List */}
        {savedPalettes.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Save className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Saved Palettes</h3>
            <p className="text-sm">Right-click on colors to build custom palettes.</p>
          </div>
        ) : filteredPalettes.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-medium mb-2">No Matching Palettes</h3>
            <p className="text-sm">Try adjusting your search query.</p>
          </div>
        ) : (
          filteredPalettes.map((palette) => (
            <div
              key={palette.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {editingPaletteId === palette.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(palette.id);
                            if (e.key === 'Escape') cancelRename();
                          }}
                          className="flex-1 px-2 py-1 text-sm font-semibold border border-blue-500 dark:border-blue-400 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          autoFocus
                        />
                        <button
                          onClick={() => saveRename(palette.id)}
                          className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelRename}
                          className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {palette.name}
                        </h4>
                        <button
                          onClick={() => startRename(palette.id, palette.name)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-all"
                          title="Rename palette"
                        >
                          <Edit2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {palette.colors.length} colors • Saved {new Date(palette.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={(event) => handleExportToVisualizer(palette.colors, palette.name, event)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Visualizer
                    </button>
                    <button
                      onClick={() => deletePalette(palette.id, palette.name)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                    >
                      <Trash2 className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <PaletteDisplay
                  colors={palette.colors}
                  onColorClick={onColorClick}
                  baseColor={selectedColor}
                  visibleFields={visibleFields}
                  colorBlindnessType={colorBlindnessType}
                  isDark={isDark}
                  addColorToPalette={addColorToPalette}
                  addColorToCustomPalette={addColorToCustomPalette}
                />
              </div>

              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                <CompactExportDropdown
                  colors={palette.colors}
                  paletteName={palette.name}
                  getCss={() => generatePaletteCss(palette.colors, palette.name)}
                  getSvg={() => generatePaletteSvg(palette.colors, palette.name)}
                />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, paletteId: null, paletteName: '' })}
        onConfirm={confirmDelete}
        title="Delete Palette"
        message={`Are you sure you want to delete "${deleteConfirmation.paletteName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default SavedPalettes;
